import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useThemeColors, getPrimaryButtonStyles } from '../hooks/useThemeColors';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { useAbortableLoad, isCanceled } from '../hooks/useAbortableLoad';
import {
  Plus, Edit, Trash2, Loader2, Route, AlertCircle, MapPin,
  Phone, Mail, MessageSquare, Globe, Users, FileText, Home, Headset, Star, ShoppingCart, Handshake,
} from 'lucide-react';

// Catálogo de iconos seleccionables para los puntos de contacto.
const ICONOS = {
  MapPin, Phone, Mail, MessageSquare, Globe, Users, FileText, Home, Headset, Star, ShoppingCart, Handshake,
};
const ICONO_OPCIONES = Object.keys(ICONOS);

// Paleta de colores sugerida para columnas / tarjetas.
const COLORES = ['#5A7A7A', '#C9A85C', '#3B82F6', '#10B981', '#8B5CF6', '#EF4444', '#F59E0B', '#64748B'];

const SIN_ETAPA = '__sin_etapa__';

const PuntoIcon = ({ nombre, className }) => {
  const Cmp = (nombre && ICONOS[nombre]) || MapPin;
  return <Cmp className={className} />;
};

const CustomerJourneyMapPage = () => {
  const { api, isAdmin } = useAuth();
  const themeColors = useThemeColors();

  const [loading, setLoading] = useState(true);
  const [puntos, setPuntos] = useState([]);
  const [etapas, setEtapas] = useState([]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const emptyForm = { nombre: '', codigo: '', etapa: '', orden: 0, descripcion: '', icono: '', color: '' };
  const [formData, setFormData] = useState(emptyForm);

  const loadEtapas = useCallback(async (signal) => {
    try {
      const res = await api.get('/experiencia/etapas', { signal });
      setEtapas(res.data || []);
    } catch (e) {
      // silencioso: las etapas son fijas en backend
    }
  }, [api]);

  const loadPuntos = useCallback(async (signal) => {
    setLoading(true);
    try {
      const res = await api.get('/experiencia/puntos-contacto', { signal });
      setPuntos(res.data || []);
    } catch (e) {
      if (isCanceled(e)) return;
      toast.error('Error al cargar los puntos de contacto');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useAbortableLoad((signal) => loadEtapas(signal), [loadEtapas]);
  useAbortableLoad((signal) => loadPuntos(signal), [loadPuntos]);

  const openCreate = (etapaPredef = '') => {
    setEditing(null);
    setFormData({ ...emptyForm, etapa: etapaPredef });
    setError('');
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setFormData({
      nombre: p.nombre || '',
      codigo: p.codigo || '',
      etapa: p.etapa || '',
      orden: p.orden ?? 0,
      descripcion: p.descripcion || '',
      icono: p.icono || '',
      color: p.color || '',
    });
    setError('');
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        nombre: formData.nombre.trim(),
        codigo: formData.codigo.trim() || null,
        etapa: formData.etapa || null,
        orden: formData.orden === '' ? 0 : parseInt(formData.orden, 10) || 0,
        descripcion: formData.descripcion.trim() || null,
        icono: formData.icono || null,
        color: formData.color || null,
      };
      if (editing) {
        await api.put(`/experiencia/puntos-contacto/${editing.id}`, payload);
        toast.success('Punto de contacto actualizado');
      } else {
        await api.post('/experiencia/puntos-contacto', payload);
        toast.success('Punto de contacto creado');
      }
      setDialogOpen(false);
      loadPuntos();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar el punto de contacto');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`¿Eliminar el punto de contacto "${p.nombre}"?`)) return;
    try {
      await api.delete(`/experiencia/puntos-contacto/${p.id}`);
      toast.success('Punto de contacto eliminado');
      loadPuntos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Columnas del journey: etapas canónicas + "Sin etapa" (si aplica).
  const columnas = [
    ...etapas.map((e) => ({ key: e, label: e })),
    { key: SIN_ETAPA, label: 'Sin etapa' },
  ];
  const puntosPorEtapa = (key) =>
    puntos.filter((p) => (key === SIN_ETAPA ? !p.etapa : p.etapa === key));

  const colorColumna = (idx) => COLORES[idx % COLORES.length];

  return (
    <div className="flex flex-col gap-5 animate-fadeIn" data-testid="customer-journey-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Route className="w-6 h-6" /> Customer Journey Map
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Puntos de contacto del cliente a lo largo de su recorrido (journey).
          </p>
        </div>
        {isAdmin() && (
          <Button
            onClick={() => openCreate()}
            size="sm"
            className="text-white"
            data-testid="agregar-punto-contacto-btn"
            {...getPrimaryButtonStyles(themeColors)}
          >
            <Plus className="w-4 h-4 mr-2" /> Agregar Punto de Contacto
          </Button>
        )}
      </div>

      {/* Journey visual */}
      {loading ? (
        <div className="p-12 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : puntos.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-12 text-center text-slate-500" data-testid="journey-empty">
            <Route className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p>Aún no hay puntos de contacto.</p>
            {isAdmin() && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => openCreate()} data-testid="journey-empty-add-btn">
                <Plus className="w-4 h-4 mr-2" /> Crear el primero
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-4 lg:gap-5 items-start" data-testid="journey-board">
          {columnas.map((col, idx) => {
              const items = puntosPorEtapa(col.key);
              if (col.key === SIN_ETAPA && items.length === 0) return null;
              const colColor = col.key === SIN_ETAPA ? '#94A3B8' : colorColumna(idx);
              return (
                <div
                  key={col.key}
                  className="w-full min-w-0 rounded-xl bg-slate-50 border border-slate-200 flex flex-col"
                  data-testid={`journey-col-${col.key}`}
                >
                  <div className="px-4 py-3 rounded-t-xl border-b border-slate-200" style={{ borderTopColor: colColor, borderTopWidth: 3 }}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm text-slate-800">{col.label}</h3>
                      <span className="text-xs text-slate-400 bg-white rounded-full px-2 py-0.5 border border-slate-200">{items.length}</span>
                    </div>
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    {items.map((p) => {
                      const cardColor = p.color || colColor;
                      return (
                        <div
                          key={p.id}
                          className="group rounded-lg bg-white border border-slate-200 p-3 hover:shadow-sm transition-shadow"
                          style={{ borderLeftColor: cardColor, borderLeftWidth: 3 }}
                          data-testid={`punto-contacto-${p.id}`}
                        >
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 shrink-0" style={{ color: cardColor }}>
                              <PuntoIcon nombre={p.icono} className="w-4 h-4" />
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-slate-900 break-words">{p.nombre}</p>
                              {p.codigo && <p className="text-[10px] font-mono text-slate-400 mt-0.5">{p.codigo}</p>}
                              {p.descripcion && <p className="text-xs text-slate-500 mt-1 break-words">{p.descripcion}</p>}
                            </div>
                          </div>
                          {isAdmin() && (
                            <div className="flex justify-end gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`editar-punto-${p.id}`} title="Editar">
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(p)} data-testid={`eliminar-punto-${p.id}`} title="Eliminar">
                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {isAdmin() && col.key !== SIN_ETAPA && (
                      <button
                        onClick={() => openCreate(col.key)}
                        className="w-full text-xs text-slate-400 hover:text-slate-700 border border-dashed border-slate-300 rounded-lg py-2 transition-colors flex items-center justify-center gap-1"
                        data-testid={`journey-add-${col.key}`}
                      >
                        <Plus className="w-3.5 h-3.5" /> Agregar aquí
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              {editing ? 'Editar Punto de Contacto' : 'Nuevo Punto de Contacto'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  data-testid="punto-nombre-input"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  maxLength={120}
                  placeholder="Ej. Visita a sala de ventas"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="codigo">Código universal</Label>
                <Input
                  id="codigo"
                  data-testid="punto-codigo-input"
                  value={formData.codigo}
                  onChange={(e) => setFormData({ ...formData, codigo: e.target.value.toUpperCase() })}
                  maxLength={40}
                  placeholder="SALA_VENTAS"
                />
                <p className="text-[11px] text-slate-400">Referenciado por plataformas externas en las encuestas.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Etapa del journey</Label>
                <Select
                  value={formData.etapa || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, etapa: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="punto-etapa-select">
                    <SelectValue placeholder="Sin etapa" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin etapa</SelectItem>
                    {etapas.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="orden">Orden</Label>
                <Input
                  id="orden"
                  type="number"
                  min="0"
                  data-testid="punto-orden-input"
                  value={formData.orden}
                  onChange={(e) => setFormData({ ...formData, orden: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Icono</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="punto-icono-picker">
                {ICONO_OPCIONES.map((nombre) => {
                  const Cmp = ICONOS[nombre];
                  const active = formData.icono === nombre;
                  return (
                    <button
                      key={nombre}
                      type="button"
                      onClick={() => setFormData({ ...formData, icono: active ? '' : nombre })}
                      className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${active ? 'border-slate-800 bg-slate-100' : 'border-slate-200 hover:bg-slate-50'}`}
                      title={nombre}
                    >
                      <Cmp className="w-4 h-4 text-slate-600" />
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="punto-color-picker">
                {COLORES.map((c) => {
                  const active = formData.color === c;
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setFormData({ ...formData, color: active ? '' : c })}
                      className={`w-8 h-8 rounded-full border-2 transition-transform ${active ? 'scale-110 border-slate-800' : 'border-white shadow-sm'}`}
                      style={{ backgroundColor: c }}
                      title={c}
                    />
                  );
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="descripcion">Descripción</Label>
              <Textarea
                id="descripcion"
                data-testid="punto-descripcion-input"
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value.slice(0, 1000) })}
                maxLength={1000}
                rows={3}
                placeholder="Notas adicionales (opcional)"
              />
              <p className="text-xs text-slate-400 text-right">{formData.descripcion.length}/1000</p>
            </div>

            <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 px-6 py-3 bg-white border-t border-slate-100">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="punto-cancelar-btn">Cancelar</Button>
              <Button type="submit" disabled={saving} className="text-white" data-testid="punto-guardar-btn" {...getPrimaryButtonStyles(themeColors)}>
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : (editing ? 'Guardar' : 'Crear')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerJourneyMapPage;
